// Plan 08-08 — reduced-motion store + matchMedia listener + data-motion mirror.
//
// REQ-reduced-motion + DEC-004 + NY-08-02:
//   $reducedMotion = OR-union of:
//     - $motionPref user override ('auto' | 'reduced' | 'full', persisted)
//     - $osReduced  OS-level prefers-reduced-motion (live matchMedia listener)
//
// Resolution rule (per UI-SPEC §"NY-08-02 OR-union truth table"):
//   pref='reduced' → true (always; user opt-in beats OS)
//   pref='full'    → false (always; user opt-out beats OS)
//   pref='auto'    → osReduced (passthrough — OS pref drives)
//
// Single source of truth: `useMotionAttributeBinding()` mirrors $reducedMotion
// onto <html data-motion="reduced|full">. NO twin stylesheets, NO per-component
// if-checks. CSS reads the attribute; PixiJS tween config + particle emitters
// read the atom directly.
//
// Pitfall 5 lock (PixiJS Ticker.speed semantics ≠ pause):
//   Ticker.speed stays at 1.0 always. Reduced motion gates at the
//   tween-config + particle-emit layer (see ticker-gate.ts + tween-config.ts +
//   particles.ts).

import { persistentAtom } from '@nanostores/persistent'
import { useStore } from '@nanostores/react'
import { atom, computed, onMount } from 'nanostores'
import { useEffect } from 'react'

export type MotionPref = 'auto' | 'reduced' | 'full'

const MOTION_PREFS: ReadonlySet<MotionPref> = new Set([
  'auto',
  'reduced',
  'full',
])

function isMotionPref(value: unknown): value is MotionPref {
  return typeof value === 'string' && MOTION_PREFS.has(value as MotionPref)
}

/**
 * User override persisted in localStorage. The default 'auto' means "follow
 * the OS pref"; an explicit 'reduced' or 'full' wins over OS.
 */
export const $motionPref = persistentAtom<MotionPref>(
  'runegambit.motion-pref',
  'auto',
  {
    encode: (v) => v,
    decode: (s) => (isMotionPref(s) ? s : 'auto'),
  },
)

/**
 * Live mirror of `window.matchMedia('(prefers-reduced-motion: reduce)')`.
 * Updated reactively by an `onMount` listener — stays at `false` in non-DOM
 * environments (SSR / Worker tests).
 */
export const $osReduced = atom<boolean>(false)

onMount($osReduced, () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function')
    return
  const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
  $osReduced.set(mq.matches)
  const handler = (e: MediaQueryListEvent | { matches: boolean }): void => {
    $osReduced.set(e.matches)
  }
  // Cast to any for cross-browser addEventListener support; the event has
  // shape `{ matches: boolean }` either way.
  mq.addEventListener('change', handler as (e: MediaQueryListEvent) => void)
  return () => {
    mq.removeEventListener(
      'change',
      handler as (e: MediaQueryListEvent) => void,
    )
  }
})

/**
 * NY-08-02 OR-union: 'reduced' overrides → true; 'full' overrides → false;
 * 'auto' passes through OS pref.
 */
export const $reducedMotion = computed(
  [$motionPref, $osReduced],
  (pref, osReduced) => {
    if (pref === 'reduced') return true
    if (pref === 'full') return false
    return osReduced
  },
)

/**
 * Mirror $reducedMotion onto <html data-motion="reduced|full"> as a single
 * source of truth. Mount once at app root inside main.tsx.
 *
 * Implementation note: we read the computed atom via @nanostores/react's
 * `useStore` so the effect runs on every change (including transitions
 * driven by matchMedia events). The attribute write is idempotent.
 */
export function useMotionAttributeBinding(): void {
  const reduced = useStore($reducedMotion)
  useEffect(() => {
    if (typeof document === 'undefined') return
    document.documentElement.setAttribute(
      'data-motion',
      reduced ? 'reduced' : 'full',
    )
  }, [reduced])
}
